import {
  ContentPagedResponse,
  NewsListParams,
  NewsView,
  NewsWriteData,
} from "@/types/content";
import { adminUrl } from "../../utils/url";
import { HttpClient } from "../HttpClient";
import { getErrorMessage } from "../errors";
import { INewsService } from "./INewsService";
import { adaptNews, adaptPagedContent } from "./adapters";
import { isAbortError } from "../errors/isAbortError";
import { CONTENT_PAGE_SIZE, buildContentQuery } from "./query";

/**
 * News articles shown in the client app.
 *
 * Note the path: news sits at `/api/admin/news`, not under
 * `/api/admin/content`, unlike banners and pages.
 */
export class NewsService implements INewsService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: adminUrl,
      timeout: 10000,
      headers: {},
    });
  }

  async list(
    params: NewsListParams = {},
  ): Promise<ContentPagedResponse<NewsView>> {
    const query = buildContentQuery(params);

    try {
      const response = await this.httpClient.get<unknown>(
        `/news?${query.toString()}`,
        params.signal ? { signal: params.signal } : {},
      );

      return adaptPagedContent(
        response,
        adaptNews,
        params.size ?? CONTENT_PAGE_SIZE,
      );
    } catch (error) {
      if (isAbortError(error)) throw error;

      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível carregar as notícias. Atualize a página e tente novamente.",
        ),
      );
    }
  }

  async getById(id: number, signal?: AbortSignal): Promise<NewsView> {
    try {
      const response = await this.httpClient.get<unknown>(
        `/news/${id}`,
        signal ? { signal } : {},
      );

      const news = adaptNews(response);
      if (!news) {
        throw new Error(
          "Não foi possível ler a notícia devolvida pelo servidor.",
        );
      }

      return news;
    } catch (error) {
      if (isAbortError(error)) throw error;

      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível carregar a notícia. Atualize e tente novamente.",
        ),
      );
    }
  }

  async create(data: NewsWriteData): Promise<number> {
    try {
      return await this.httpClient.post<number>(`/news`, data, {}, true);
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível criar a notícia. Confirme o título e o conteúdo.",
        ),
      );
    }
  }

  async update(id: number, data: NewsWriteData): Promise<void> {
    try {
      await this.httpClient.put<void>(`/news/${id}`, data);
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível atualizar a notícia. Tente guardar novamente.",
        ),
      );
    }
  }

  async delete(id: number): Promise<void> {
    try {
      await this.httpClient.delete<void>(`/news/${id}`);
    } catch (error) {
      throw new Error(
        getErrorMessage(
          error,
          "Não foi possível eliminar a notícia. Tente novamente.",
        ),
      );
    }
  }
}
